import {
  CollectionsFetchDocument,
  CollectionsFetchQuery,
  CollectionsFetchQueryVariables,
  useCollectionDeleteMutation,
} from 'lib-knowledge-management-common/graphql';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { ampli } from 'lib-shared-tracking';
import { useNavigate } from 'react-router-dom';

type MutationFunction = ReturnType<typeof useCollectionDeleteMutation>[0];
type MutationReturnValue = ReturnType<MutationFunction>;
type DeleteFn = (data: {
  collectionId: string;
  collectionTitle: string;
}) => MutationReturnValue;
type UseCollectionDeleteReturnValue = [
  DeleteFn,
  ReturnType<typeof useCollectionDeleteMutation>[1],
];

/**
 * Returns a function for deleting a collection and the result of the delete
 * mutation. The returned function will delete the collection with the given ID
 * and update the collections list in the cache, then trigger analytics events,
 * navigate to the collections list page, and show a notification message.
 *
 * @param {object} - An object with a `closeModal` function for closing a modal.
 * @returns {UseCollectionDeleteReturnValue} - An array with the delete
 *   collection function and the result of the collection delete mutation.
 */
export function useCollectionDelete({
  closeModal,
}: {
  closeModal: () => void;
}): UseCollectionDeleteReturnValue {
  const [deleteMutation, mutationResult] = useCollectionDeleteMutation();
  const navigate = useNavigate();
  const { successNotify, errorNotify } = useNotification();

  const deleteCollection: DeleteFn = ({ collectionId, collectionTitle }) => {
    return deleteMutation({
      variables: {
        id: collectionId,
      },
      update: (client) => {
        const collectionList = client.readQuery<
          CollectionsFetchQuery,
          CollectionsFetchQueryVariables
        >({
          query: CollectionsFetchDocument,
        });
        if (!collectionList) return;

        const updatedResults = [...collectionList.collections.results].filter(
          (result) => result.id !== collectionId,
        );

        const updatedCollectionList: CollectionsFetchQuery = {
          ...collectionList,
          collections: {
            ...collectionList.collections,
            results: updatedResults,
          },
        };

        client.writeQuery<
          CollectionsFetchQuery,
          CollectionsFetchQueryVariables
        >({
          query: CollectionsFetchDocument,
          data: updatedCollectionList,
        });
      },
    })
      .then((args) => {
        closeModal();
        ampli.knowledgeCollectionDestroyed({ collectionId });
        successNotify({
          title: 'Collection deleted',
          text: `"${collectionTitle}" has been deleted`,
        });
        navigate('/knowledge/collection');

        return args;
      })
      .catch((err) => {
        const message = typeof err?.message === 'string' ? err.message : '';
        const text = message.includes(
          'cannot delete collection with linked kbs',
        )
          ? 'This collection is associated with a Help Center and cannot be removed. Please remove the association from a Help Center first.'
          : 'Please try again. If this issue persists, please contact customer support.';

        errorNotify({
          text,
          title: 'Unable to delete collection',
          time: 10000,
        });
        return err;
      });
  };

  return [deleteCollection, mutationResult];
}
